import React from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../../components/layout/AppLayout';
import { dummyData } from './data';
import DashboardHeader from './DashboardHeader';
import MonthlyProgress from './MonthlyProgress';
import StageProcess from './StageProcess';
import NextMonthFocus from './NextMonthFocus';
import MonthlyInsights from './MonthlyInsights';
import KeyDelays from './KeyDelays';
import { useProjectsList } from '../../../../../../contexts/ProjectsContext';
import Loader from '../../../../../shared/components/ui/Loader';
import { getOperationsMonthlyStats } from '../../../../services/api/implementation/impl';
import toast from 'react-hot-toast';
import ErrorScreen from '../../../../../shared/components/ui/ErrorScreen';
import useOperationsReportPdf from '../useOperationsReportPdf';

// Selected month → API gets 1st of next month (Jan → 1 Feb; Dec 2025 → 1 Jan 2026), same as inventory monthly
const toReportRunDate = (selectedDate) => {
    const nextMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 1);
    const y = nextMonth.getFullYear();
    const m = String(nextMonth.getMonth() + 1).padStart(2, '0');
    return `${y}-${m}-01`;
};

const getErrorMessage = (error) => {
    if (!error) return 'Unable to load monthly report.';
    const msg = error?.response?.data?.message || error?.message;
    if (msg && typeof msg === 'string') {
        const reportRunDateMatch = msg.match(/reportRunDate must be on or after (\d{4}-\d{2}-\d{2}) for MONTHLY reports/i);
        if (reportRunDateMatch) {
            const minDate = reportRunDateMatch[1];
            return `Monthly report is only available from ${minDate} onwards. Please select a month that ends on or after ${minDate} to view the report.`;
        }
        return msg;
    }
    return 'Unable to load monthly report. Please try again.';
};

const MonthlyReports = () => {
    const navigate = useNavigate();
    const { projectsForDropdown, projectsError } = useProjectsList();
    const projects = projectsForDropdown?.length > 0 ? projectsForDropdown : [{ label: "No Project", id: null, value: null }];

    const [currentProject, setCurrentProject] = React.useState(projects[0]?.label || "No Project");
    // Default to first day of previous month (same as inventory monthly; current/future months not selectable)
    const [selectedDate, setSelectedDate] = React.useState(() => {
        const now = new Date();
        return new Date(now.getFullYear(), now.getMonth() - 1, 1);
    });
    const [loading, setLoading] = React.useState(false);
    const [reportData, setReportData] = React.useState(null);
    const [reportMonthText, setReportMonthText] = React.useState('');
    const [reportError, setReportError] = React.useState(null);

    // Check for project API error
    React.useEffect(() => {
        if (projectsError) {
            const message = projectsError?.response?.data?.message || projectsError?.message || 'Unable to load projects.';
            setReportError(message);
            toast.error(message);
        }
    }, [projectsError]);


    React.useEffect(() => {
        if (projects?.length > 0 && projects[0]?.id !== null && currentProject === "No Project") {
            setCurrentProject(projects[0]?.label);
        }
    }, [projects, currentProject]);

    const fetchMonthlyStats = React.useCallback(async () => {
        const projectObj = projectsForDropdown?.find(p => p.label === currentProject);
        const projectId = projectObj?.id;

        if (!projectId) return;

        setReportError(null);
        setLoading(true);
        try {
            const response = await getOperationsMonthlyStats(projectId, toReportRunDate(selectedDate));
            if (response && response.data) {
                setReportData(response.data);
            } else {
                setReportData(null);
            }
        } catch (error) {
            console.error("Failed to fetch monthly stats", error);
            const message = getErrorMessage(error);
            setReportError(message);
            setReportData(null);
            toast.error(message);
        } finally {
            setLoading(false);
        }
    }, [selectedDate, currentProject, projectsForDropdown]);

    React.useEffect(() => {
        fetchMonthlyStats();
    }, [fetchMonthlyStats]);

    const { downloadPdf, isDownloadingPdf } = useOperationsReportPdf('monthly');
    const handleDownloadPdf = React.useCallback(() => {
        const projectId = projectsForDropdown?.find(p => p.label === currentProject)?.id;
        downloadPdf(projectId, toReportRunDate(selectedDate));
    }, [downloadPdf, projectsForDropdown, currentProject, selectedDate]);

    React.useEffect(() => {
        if (reportData?.lastMonth?.start) {
            try {
                const startDate = new Date(reportData.lastMonth.start);
                const monthYear = startDate.toLocaleString('default', { month: 'long', year: 'numeric' });
                setReportMonthText(`Report Month: ${monthYear}`);
            } catch (e) {
                setReportMonthText('');
            }
        } else {
            setReportMonthText('');
        }
    }, [reportData]);

    // Transform API data for components
    const getMonthlyProgressData = () => {
        if (!reportData?.progressBlock) return dummyData.monthlyProgress;

        const { progressBlock, snapshotBlock } = reportData;
        const totalDelay = (snapshotBlock?.delayedAtStart || 0) + (snapshotBlock?.delayedDuringTask || 0);

        return {
            planned: Math.round(Number(progressBlock.plannedPercent) || 0),
            actual: Math.round(Number(progressBlock.actualPercent) || 0),
            variance: Math.round(Number(progressBlock.variance) || 0),
            delayDays: Math.round(Number(totalDelay) || 0),
            executionScore: Math.round(Number(progressBlock.executionScore) || 0)
        };
    };

    const getStageProcessData = () => {
        if (!reportData?.snapshotBlock?.stages) return [];

        const colors = ["#1F4B55", "#E3C86F", "#7FA0A5", "#FF7676", "#1F4B55", "#E3C86F"];

        return reportData.snapshotBlock.stages.map((stage, index) => ({
            name: stage.stage,
            planned: Math.round(Number(stage.plannedPercent) || 0),
            actual: Math.round(Number(stage.actualPercent) || 0),
            color: colors[index % colors.length]
        }));
    };


    // Key Delays Data
    const getKeyDelaysData = () => {
        const delayedAtStart = reportData?.monthlyDelayed?.delayedAtStart || [];
        const delayedDuringTask = reportData?.monthlyDelayed?.delayedDuringTask || [];
    
        const allDelayedTasks = [...delayedAtStart, ...delayedDuringTask];
    
        return allDelayedTasks.map((task, index) => {
            // calculate delay days
            let delayDays = 0;
    
            if (task.delayAtStartDays !== undefined) {
                delayDays = task.delayAtStartDays;
            } else if (task.planned_start_date && task.actual_start_date) {
                const planned = new Date(task.planned_start_date);
                const actual = new Date(task.actual_start_date);
                delayDays = Math.max(
                    0,
                    Math.ceil((actual - planned) / (1000 * 60 * 60 * 24))
                );
            }
    
            return {
                id: task.id || index + 1,
                title: task.name + " - " + task.workType?.name,
                reason:
                    task.actual_start_date === null
                        ? "Not started on planned date"
                        : "Started late",
                responsible: task.assignedEngineer?.name || "Unassigned",
                days: delayDays,
                status: task.status,
                stage: task.workType?.name
            };
        });
    };
    

    const monthlyProgressData = reportData ? getMonthlyProgressData() : (loading ? null : dummyData.monthlyProgress);
    const stageProcessData = reportData ? getStageProcessData() : (loading ? [] : dummyData.stageProcess);
    const keyDelaysData = reportData ? getKeyDelaysData() : (loading ? [] : dummyData.keyDelays);


    return (
        <Layout>
            <Loader isVisible={loading} />
            <div className="bg-[#F8FAFC] min-h-screen">
                <div className="sticky top-0 z-40 bg-[#EEF3F7] backdrop-blur pt-6 px-6">
                    <DashboardHeader
                        projectName={currentProject}
                        onProjectChange={setCurrentProject}
                        lastUpdated={dummyData.lastUpdated}
                        date={selectedDate}
                        onDateChange={setSelectedDate}
                        projects={projects}
                        reportMonthText={reportMonthText}
                        onViewSample={() => navigate('/builder/operations/monthly-reports/sample')}
                        onDownloadPdf={handleDownloadPdf}
                        isDownloadingPdf={isDownloadingPdf}
                        canDownloadPdf={!!reportData && !reportError && !loading}
                    />
                </div>
                <div className="px-6 pb-6 max-w-[1600px] mx-auto">
                    {reportError ? (
                        <ErrorScreen
                            title="Unable to Load Monthly Report"
                            message={reportError}
                            onRetry={() => window.location.reload()}
                            isRetrying={loading}
                        />
                    ) : (
                        <div className="flex flex-col gap-6">

                            {/* Header Tabs */}
                            <div className="flex">
                                <div
                                    className="bg-[#D4B95E] text-white px-10 py-3 font-medium text-base relative z-10 pr-16"
                                    style={{ clipPath: 'polygon(0 0, 100% 0, 85% 100%, 0% 100%)' }}
                                >
                                    Progress Overview
                                </div>
                                <div
                                    className="bg-[#1E4C56] text-white px-10 py-3 font-medium text-base -ml-10 pl-16 relative z-0 pr-16"
                                    style={{ clipPath: 'polygon(0 0, 100% 0, 85% 100%, 0% 100%)' }}
                                >
                                    Minor Delays
                                </div>
                            </div>
                            {/* Title */}
                            <h1 className="text-2xl font-bold text-gray-900">Monthly Progress Report</h1>

                            {/* Top Row: Monthly Progress Cards */}
                            <MonthlyProgress data={monthlyProgressData} />

                            {/* Middle Section: Split Layout */}
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
                                {/* Left Column: Stage Process */}
                                <div>
                                    <StageProcess stages={stageProcessData} />
                                </div>

                                {/* Right Column: Next Month Focus & Insights & Key Delays */}
                                <div className="flex flex-col gap-6 h-full">
                                    {/* <NextMonthFocus focusItems={dummyData.nextMonthFocus} />
                                    <MonthlyInsights insights={dummyData.monthlyInsights} /> */}
                                    <KeyDelays delays={keyDelaysData} />
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default MonthlyReports;
